import { Task, delay } from 'redux-saga';
import { take, fork, cancelled } from 'redux-saga/effects';
import { runInAction } from 'mobx';
import { PayerOnboardingUserAction } from './payer-onboarding-user-actions';
import { IPayerOnboardingMobileNumberRequest } from './payer-onboarding-app-state';
import { ActionHandlers, AsyncActionHandler, ActionConcurrencyInstruction, callInAction, ActionConfig } from '../Shared/utils/saga-utils';
import { PayerOnboardingPage } from './pages';
import { scrollErrorIntoViewOnNextFrame } from '../WebGiving/utils/error-focuser';
import { confirmationCodeSentMessage } from '../WebGiving/helpers/notification-message-helper';
import { NotificationViewModel } from '../WebGiving/components/notification/notification';
import { PageConfirmMobileNumberViewModel } from './pages/page-confirm-mobile-number/page-confirm-mobile-number-view-model';
import { PagePaymentMethodViewModel } from './pages/page-payment-method/page-payment-method';
import { PageEnterDetailsViewModel } from './pages/page-enter-details/page-enter-details-view-model';
import { PageSuccessViewModel } from './pages/page-success/page-success-view-model';
import { PageConfirmBankAccountViewModel } from './pages/page-confirm-bank-account/page-confirm-bank-account-view-model';
import { ModalType } from './modals/modal-type';
import { IPayerOnboardingSagaContext } from './payer-onboarding-saga-context';
import { PayerOnboardingErrorType } from './components/error-message/payer-onboarding-error';
import * as Models from './payer-onboarding-generated';
import { ResetPageReason, PaymentMethodType } from './payer-onboarding-generated';
import { addQueryStringParameter } from '../Shared/utils/url-utils';

function createActionHandlers() {
	const actionHandlers = new ActionHandlers<IPayerOnboardingSagaContext, typeof PayerOnboardingUserAction[keyof typeof PayerOnboardingUserAction]>();

	const submitCurrentPageConfig: Partial<ActionConfig> = {
		blocking: true,
		priority: 10,
	};

	actionHandlers.add(
		PayerOnboardingUserAction.SubmitCurrentPage, {
			...submitCurrentPageConfig,
			async: true,
			handler: (context) => handleSubmitCurrentPageSaga(context),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.ChangeMobileNumber, {
			async: false,
			handler: (context) => handleChangeMobileNumberSaga(context),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.PreviousPage, {
			async: false,
			handler: (context) => handlePreviousPageSaga(context),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.RequestConfirmationCode, {
			...submitCurrentPageConfig,
			async: true,
			handler: (context, action) => requestConfirmationCode(context, action.useVoiceCall),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.RequestResendOfConfirmationCode, {
			async: true,
			handler: (context, action) => requestResendOfConfirmationCode(context, action.useVoiceCall),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.OpenModalDialog, {
			async: false,
			handler: (context, action) => handleOpenModal(context, action.modalType),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.ResetPageBecauseOfError, {
			async: false,
			handler: (context, action) => handleResetPageBecauseOfError(context, action.reason),
		});
		actionHandlers.add(
			PayerOnboardingUserAction.GiveNow, {
				...submitCurrentPageConfig,
				async: true,
				handler: (context, action) => handleGiveNowSaga(context),
			});
		actionHandlers.add(
			PayerOnboardingUserAction.Login, {
				...submitCurrentPageConfig,
				async: true,
				handler: (context, action) => handleLoginSaga(context),
			});
			actionHandlers.add(
		PayerOnboardingUserAction.SkipPaymentMethod, {
			async: false,
			handler: (context, action) => handleSkipPaymentSaga(context),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.ChangeBankDetails, {
			async: false,
			handler: (context, action) => handleChangeBankDetailsSaga(context),
		});
	actionHandlers.add(
		PayerOnboardingUserAction.FetchAppContentCard, {
			async: true,
			handler: context => handleAppCardContent(context),
		});

	return actionHandlers;
}

function* handleSubmitCurrentPageSaga(context: IPayerOnboardingSagaContext): IterableIterator<any> {
	const { mainViewModel } = context;
	const { currentPageViewModel } = mainViewModel;

	if (context.disableValidation !== true) {
		const currentValidationState = currentPageViewModel.validate();
		if (currentValidationState.isValid !== true) {
			currentPageViewModel.validationState = currentValidationState;
			scrollErrorIntoViewOnNextFrame();
			return;
		}
	}

	currentPageViewModel.isLoading = true;
	try {
		switch (currentPageViewModel.type) {
			case PayerOnboardingPage.EnterDetails:
				yield callInAction(enterDetailsSaga, context, currentPageViewModel as PageEnterDetailsViewModel);
				break;
			case PayerOnboardingPage.ConfirmMobileNumber:
				yield callInAction(confirmNumberSaga, context, currentPageViewModel as PageConfirmMobileNumberViewModel);
				break;
			case PayerOnboardingPage.PaymentMethod:
				const paymentMethodViewModel = currentPageViewModel as PagePaymentMethodViewModel;
				switch (paymentMethodViewModel.paymentMethodType) {
					case PaymentMethodType.CreditCard:
						yield callInAction(addCreditCardPaymentMethodSaga, context, paymentMethodViewModel);
						break;
					case PaymentMethodType.ACH:
						yield callInAction(addAchPaymentMethodSaga, context, paymentMethodViewModel);
						break;
				}
				break;
			case PayerOnboardingPage.ConfirmBankAccount:
				yield callInAction(confirmBankAccountSaga, context, currentPageViewModel as PageConfirmBankAccountViewModel);
				break;
		}
	} finally {
		if (currentPageViewModel === mainViewModel.currentPageViewModel) {
			// only disable loading if we're still on the same page, otherwise keep isLoading to avoid unnecessary transitions
			currentPageViewModel.isLoading = false;
		}
	}
}

function* addCreditCardPaymentMethodSaga(context: IPayerOnboardingSagaContext, currentPageViewModel: PagePaymentMethodViewModel): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { MerchantInfo: { PaymentLabel, Handle, MobileAppName, HasCustomApp }, InvitationToken } = mainViewModel.viewData;

	const response: Models.AddPaymentMethodResponse | Models.AddPaymentMethodErrorResponse = yield dataService.addCreditCardPaymentMethod({
		model: {
			MerchantHandle: Handle,
			InvitationToken: InvitationToken,
			CardNumber: currentPageViewModel.cardModel.cardNumber.value.replace(/\D/g, ''),
			CardExpirationMonth: currentPageViewModel.cardModel.cardExpirationMonth,
			CardExpirationYear: currentPageViewModel.cardModel.cardExpirationYear,
			CardSecurity: currentPageViewModel.cardModel.cvv.value,
			ZipCode: currentPageViewModel.cardModel.zipCode.value,
		},
	}, appState);

	if (response.Success !== true) {
		currentPageViewModel.setError({
			errorType: PayerOnboardingErrorType.AddPaymentMethodError,
			errorResultCode: response.ResultCode,
			errorMessageLines: response.ErrorMessage,
		});
		return;
	}

	mainViewModel.openPage(new PageSuccessViewModel(
		appState.firstName,
		PaymentLabel,
		appState.getAppUrl,
		MobileAppName,
		HasCustomApp,
		appState.appLinkSmsSent,
		appState.appContentCards,
	));

	mainViewModel.showEmailNotification(appState.enteredEmailAddress);
}

function* addAchPaymentMethodSaga(context: IPayerOnboardingSagaContext, currentPageViewModel: PagePaymentMethodViewModel): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { MerchantInfo: { Handle }, InvitationToken } = mainViewModel.viewData;
	const { achModel } = currentPageViewModel;

	const response: Models.AddAchPaymentMethodResponse | Models.AddAchPaymentMethodErrorResponse = yield dataService.addAchPaymentMethod({
		model: {
			MerchantHandle: Handle,
			InvitationToken: InvitationToken,
			RoutingNumber: achModel.routingNumber.value,
		},
	}, appState);

	if (response.Success !== true) {
		currentPageViewModel.achModel.routingNumber.setServerSideValidationError('This routing number is not valid');
		return;
	}

	mainViewModel.openPage(new PageConfirmBankAccountViewModel(
		achModel,
		response.BankName,
	));
}

function* confirmBankAccountSaga(context: IPayerOnboardingSagaContext, currentPageViewModel: PageConfirmBankAccountViewModel): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { MerchantInfo, InvitationToken } = mainViewModel.viewData;
	const { Handle, PaymentLabel, MobileAppName, HasCustomApp } = MerchantInfo;
	const { routingNumber, accountNumber, accountType } = currentPageViewModel.achModel;

	if (context.disableValidation || currentPageViewModel.achModel.accountNumber.value === currentPageViewModel.accountNumberConfirm.value) {
		const response: Models.AddPaymentMethodResponse | Models.AddPaymentMethodErrorResponse = yield dataService.confirmAchAccount({
			model: {
				MerchantHandle: Handle,
				InvitationToken: InvitationToken,
				RoutingNumber: routingNumber.value,
				AccountNumber: accountNumber.value,
				AccountType: accountType.value,
			},
		}, appState);

		if (response.Success !== true) {
			const vm = new PagePaymentMethodViewModel(
				MerchantInfo,
				appState.personCountryInfo,
				PaymentMethodType.ACH,
				currentPageViewModel.achModel,
			);

			currentPageViewModel.error = {
				errorType: PayerOnboardingErrorType.AddPaymentMethodError,
				errorResultCode: response.ResultCode,
				errorMessageLines: response.ErrorMessage,
			};
			mainViewModel.openPage(vm);
			return;
		}

		mainViewModel.openPage(new PageSuccessViewModel(
			appState.firstName,
			PaymentLabel,
			appState.getAppUrl,
			MobileAppName,
			HasCustomApp,
			appState.appLinkSmsSent,
			appState.appContentCards,
		));

		mainViewModel.showEmailNotification(appState.enteredEmailAddress);
	} else {
		currentPageViewModel.achModel.accountNumber.updateValue('');
		currentPageViewModel.achModel.updateAccountMismatchError(true);
		mainViewModel.openPage(new PagePaymentMethodViewModel(
			MerchantInfo,
			appState.personCountryInfo,
			PaymentMethodType.ACH,
			currentPageViewModel.achModel,
		));
	}
}

function* confirmNumberSaga(context: IPayerOnboardingSagaContext, currentPageViewModel: PageConfirmMobileNumberViewModel): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { firstName, lastName, mobileNumberRequest, enteredEmailAddress } = appState;
	const token = mobileNumberRequest && mobileNumberRequest.mobileRequestToken || '';
	const { MerchantInfo, InvitationToken, SkipAddPaymentMethod } = mainViewModel.viewData;
	const { Handle, PaymentLabel, GetAppUrl, MobileAppName, HasCustomApp } = MerchantInfo;
	const browserTimeZone = (window.Intl && typeof (window.Intl) === 'object' && Intl.DateTimeFormat().resolvedOptions().timeZone) || null;

	const response: Models.ConfirmCodeAndUpdateDetailsResponse | Models.ConfirmCodeAndUpdateDetailsErrorResponse = yield dataService.confirmCodeAndUpdateDetails({
		model: {
			MerchantHandle: Handle,
			InvitationToken: InvitationToken,
			Code: currentPageViewModel.securityCode.value,
			RequestToken: token,
			FirstName: firstName,
			LastName: lastName,
			BrowserTimeZone: browserTimeZone,
			Email: enteredEmailAddress,
		},
	}, appState);

	if (response.Success !== true) {
		if (NewFeatures.EnhancedRedirectUX && response.LoginUrl) {
			currentPageViewModel.error = {
				message: response.Message,
				errorType: PayerOnboardingErrorType.AccountAlreadyActiveError,
				loginUrl: response.LoginUrl,
			};
		} else if (response.ConfirmationCodeInvalid) {
			currentPageViewModel.securityCode.setServerSideValidationError(response.Message);
		} else {
			if (response.Reason === ResetPageReason.Unknown) {
				currentPageViewModel.error = {
					errorType: PayerOnboardingErrorType.Generic,
					message: response.Message,
				};
			} else {
				currentPageViewModel.error = {
					errorType: PayerOnboardingErrorType.ShouldReloadPage,
					reason: response.Reason,
				};
			}
		}
		return;
	}

	appState.loginToken = response.LoginToken;

	appState.getAppUrl = response.AppConnectData ? addQueryStringParameter(GetAppUrl, 'data', response.AppConnectData) : GetAppUrl;
	appState.appLinkSmsSent = response.AppLinkSMSHasBeenSent;

	if (!response.IdentityToken) {
		throw new Error('Identity token should always be set when PayerOnboarding_Enhancements is true');
	}
	appState.identityToken = response.IdentityToken;
	appState.personCountryInfo = response.PersonCountryInfo;


	const nextPage = SkipAddPaymentMethod
		? new PageSuccessViewModel(appState.firstName, PaymentLabel, appState.getAppUrl, MobileAppName, HasCustomApp, appState.appLinkSmsSent, appState.appContentCards)
		: new PagePaymentMethodViewModel(MerchantInfo, response.PersonCountryInfo);

	mainViewModel.openPage(nextPage);
}

function* enterDetailsSaga(context: IPayerOnboardingSagaContext, currentPageViewModel: PageEnterDetailsViewModel): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { MerchantInfo: { Handle }, InvitationToken } = mainViewModel.viewData;

	const response: Models.EnterMobileNumberResponse | Models.EnterMobileNumberErrorResponse = yield dataService.enterMobileNumber({
		model: {
			MerchantHandle: Handle,
			InvitationToken: InvitationToken,
			MobileNumber: currentPageViewModel.mobileNumber.value,
			Country: Models.Country[currentPageViewModel.mobileCountry.value],
			UseVoiceCall: appState.userPrefersVoiceCalls,
		},
	}, appState);

	if (response.Success !== true) {
		if (response.Reason === Models.InvalidMobileNumberReason.MobileNumberInUse) {
			currentPageViewModel.error = {
				errorType: PayerOnboardingErrorType.MobileNumberInUseError,
				loginUrl: response.LoginUrl,
			};
		} else {
			currentPageViewModel.mobileNumber.setServerSideValidationError(response.Message);
		}
		return;
	}

	appState.mobileNumberRequest = {
		mobileNumber: response.MobileNumber,
		mobileRequestToken: response.RequestToken,
	};
	appState.firstName = currentPageViewModel.firstName.value;
	appState.lastName = currentPageViewModel.lastName.value;
	appState.enteredMobileNumber = currentPageViewModel.mobileNumber.value;
	appState.enteredEmailAddress = currentPageViewModel.emailAddress.value;
	appState.country = currentPageViewModel.mobileCountry.value;

	mainViewModel.openPage(new PageConfirmMobileNumberViewModel(
		response.MobileNumber.NationalNumber,
		mainViewModel.resendCodeDisabled,
	));

	const notificationViewModel = new NotificationViewModel(confirmationCodeSentMessage(appState.userPrefersVoiceCalls), 10);
	mainViewModel.showNotification(notificationViewModel);
}

function handleOpenModal(context: IPayerOnboardingSagaContext, modalType: ModalType) {
	context.mainViewModel.openModal(modalType);
}

function handleResetPageBecauseOfError(context: IPayerOnboardingSagaContext, reason: ResetPageReason | undefined) {
	const { mainViewModel } = context;

	if (reason && reason === ResetPageReason.DuplicateMobileNumber) {
		mainViewModel.openPage(rehydrateEnterDetailsViewModelFromSagaContext(context));
	} else {
		window.location.reload();
	}
}

function* handleGiveNowSaga(context: IPayerOnboardingSagaContext) {
	const { appState, dataService, mainViewModel } = context;
	const { loginToken } = appState;
	const { InvitationToken, MerchantInfo } = context.mainViewModel.viewData;

	if (!loginToken) {
		mainViewModel.reportError('Attempt to log in without login token');
		return;
	}

	const response: Models.LoginResponse = yield dataService.giveNow({
		model: {
			MerchantHandle: MerchantInfo.Handle,
			LoginToken: loginToken,
			InvitationToken: InvitationToken || '',
		},
	}, appState);

	window.location.href = response.ReturnUrl;
}

function* handleLoginSaga(context: IPayerOnboardingSagaContext) {
	const { appState, dataService, mainViewModel } = context;
	const { loginToken } = appState;
	const { InvitationToken } = context.mainViewModel.viewData;

	if (!loginToken) {
		mainViewModel.reportError('Attempt to log in without login token');
		return;
	}

	const response: Models.LoginResponse = yield dataService.login({
		model: {
			LoginToken: loginToken,
			InvitationToken: InvitationToken || '',
		},
	}, appState);

	window.location.href = response.ReturnUrl;
}

function handleChangeBankDetailsSaga(context: IPayerOnboardingSagaContext) {
	const { mainViewModel } = context;
	const viewModel = rehydratePaymentMethodViewModelFromSagaContext(context);
	mainViewModel.openPage(viewModel);
}

function rehydratePaymentMethodViewModelFromSagaContext(context: IPayerOnboardingSagaContext): PagePaymentMethodViewModel {
	const { mainViewModel, appState } = context;
	const { MerchantInfo } = mainViewModel.viewData;
	const { achModel } = mainViewModel.currentPageViewModel as PageConfirmBankAccountViewModel;

	return new PagePaymentMethodViewModel(
		MerchantInfo,
		appState.personCountryInfo,
		PaymentMethodType.ACH,
		achModel,
	);
}

function handleSkipPaymentSaga(context: IPayerOnboardingSagaContext) {
	const { mainViewModel, appState } = context;
	const { MerchantInfo: { PaymentLabel, MobileAppName, HasCustomApp } } = mainViewModel.viewData;

	mainViewModel.openPage(new PageSuccessViewModel(
		appState.firstName,
		PaymentLabel,
		appState.getAppUrl,
		MobileAppName,
		HasCustomApp,
		appState.appLinkSmsSent,
		appState.appContentCards,
	));

	mainViewModel.showEmailNotification(appState.enteredEmailAddress);
}

function handleChangeMobileNumberSaga(context: IPayerOnboardingSagaContext) {
	const { mainViewModel } = context;
	const newViewModel = rehydrateEnterDetailsViewModelFromSagaContext(context);
	mainViewModel.openPage(newViewModel);
}

function handlePreviousPageSaga(context: IPayerOnboardingSagaContext) {
	const { mainViewModel } = context;

	switch (mainViewModel.currentPageViewModel.type) {
		case PayerOnboardingPage.EnterDetails:
			break;
		case PayerOnboardingPage.ConfirmMobileNumber:
			const enterDetailsViewModel = rehydrateEnterDetailsViewModelFromSagaContext(context);
			mainViewModel.openPage(enterDetailsViewModel);
			break;
		case PayerOnboardingPage.ConfirmBankAccount:
			const paymentMethodViewModel = rehydratePaymentMethodViewModelFromSagaContext(context);
			mainViewModel.openPage(paymentMethodViewModel);
			break;
	}
}

function* requestConfirmationCode(context: IPayerOnboardingSagaContext, userPrefersVoiceCalls: boolean | undefined) {
	const { mainViewModel, appState } = context;
	const currentPage = mainViewModel.currentPageViewModel;

	if (typeof userPrefersVoiceCalls === 'boolean') {
		appState.userPrefersVoiceCalls = userPrefersVoiceCalls;
	}

	if (currentPage.type !== PayerOnboardingPage.EnterDetails) {
		throw new Error(`Expected '${PayerOnboardingPage[PayerOnboardingPage.EnterDetails]}' page but got '${PayerOnboardingPage[currentPage.type]}'`);
	}

	yield callInAction(handleSubmitCurrentPageSaga, context);
}

function* requestResendOfConfirmationCode(context: IPayerOnboardingSagaContext, userPrefersVoiceCalls: boolean | undefined) {
	const { mainViewModel, appState, dataService } = context;
	const currentPage = mainViewModel.currentPageViewModel;
	const { MerchantInfo: { Handle }, InvitationToken } = mainViewModel.viewData;

	if (typeof userPrefersVoiceCalls === 'boolean') {
		appState.userPrefersVoiceCalls = userPrefersVoiceCalls;
	}

	if (currentPage.type !== PayerOnboardingPage.ConfirmMobileNumber) {
		throw new Error(`Expected '${PayerOnboardingPage[PayerOnboardingPage.ConfirmMobileNumber]}' page but got '${PayerOnboardingPage[currentPage.type]}'`);
	}

	const mobileNumberRequest = ensureMobileNumberRequest(appState.mobileNumberRequest);

	mainViewModel.resendCodeDisabled.set(true);

	try {
		const resendResponse: Models.EnterMobileNumberResponse | Models.EnterMobileNumberErrorResponse = yield dataService.resendConfirmationCode({
			model: {
				MerchantHandle: Handle,
				InvitationToken: InvitationToken,
				NormalizedInternationalNumber: mobileNumberRequest.mobileNumber.NormalizedInternationalNumber,
				UseVoiceCall: appState.userPrefersVoiceCalls,
			},
		}, appState);

		if (resendResponse.Success !== true) {
			currentPage.error = {
				errorType: PayerOnboardingErrorType.Generic,
				message: resendResponse.Message,
			};
			return;
		}

		appState.mobileNumberRequest = {
			mobileNumber: resendResponse.MobileNumber,
			mobileRequestToken: resendResponse.RequestToken,
		};

		const notificationText = confirmationCodeSentMessage(appState.userPrefersVoiceCalls);

		const notificationViewModel = new NotificationViewModel(notificationText, 10);
		mainViewModel.showNotification(notificationViewModel);

		yield delay(20000);
	}
	finally {
		mainViewModel.resendCodeDisabled.set(false);
	}

}

function* handleAppCardContent(context: IPayerOnboardingSagaContext) {
	const { dataService, appState } = context;
	const result = yield dataService.getAppContentCards({}, appState);
	appState.appContentCards = result.Data;
}

function ensureMobileNumberRequest(mobileNumberRequest: IPayerOnboardingMobileNumberRequest | null) {
	if (!mobileNumberRequest) {
		throw new Error('Illegal state: mobile number request must be defined');
	}

	return mobileNumberRequest;
}

export function* payerOnboardingSaga(context: IPayerOnboardingSagaContext): IterableIterator<any> {
	const { userActionChannel, mainViewModel } = context;
	const { reportError } = mainViewModel;
	const actionHandlers = createActionHandlers();

	let currentAsyncHandler: AsyncActionHandler<IPayerOnboardingSagaContext> | undefined;
	let currentAsyncTask: Task | undefined;

	while (true) {
		const userAction: PayerOnboardingUserAction = yield take(userActionChannel);
		try {
			const incomingActionHandler = actionHandlers.get(userAction);

			if (currentAsyncHandler && currentAsyncTask && currentAsyncTask.isRunning()) {
				const instruction = actionHandlers.getInstructionForIncomingAction(currentAsyncHandler, incomingActionHandler);

				switch (instruction) {
					case ActionConcurrencyInstruction.AllowIncoming:
						console.log('Allowing incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						break;
					case ActionConcurrencyInstruction.CancelRunning:
						console.log('Cancelling running action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						if (currentAsyncHandler.blocking) {
							context.executingBlockingAction = false;
						}
						currentAsyncTask.cancel();
						currentAsyncHandler = undefined;
						break;
					case ActionConcurrencyInstruction.BlockIncoming:
						console.log('Blocking incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						continue;
					default:
						throw new Error(`ActionConcurrencyInstruction[${instruction}] is not supported`);
				}
			}

			console.log('Starting action', { incoming: incomingActionHandler.actionType.name });
			if (incomingActionHandler.async) {
				currentAsyncHandler = incomingActionHandler;

				if (currentAsyncHandler.blocking) {
					context.executingBlockingAction = true;
				}

				currentAsyncTask = yield fork(function* () {
					try {
						yield callInAction(incomingActionHandler.handler, context, userAction);
					} catch (ex) {
						reportError(ex, {
							sagaRunningFork: true,
							sagaUserAction: getUserActionName(userAction),
						});
					} finally {
						if (!(yield cancelled())) {
							context.executingBlockingAction = false;
						}
					}
				});
			} else {
				runInAction(() => incomingActionHandler.handler(context, userAction));
			}
		} catch (ex) {
			reportError(ex, {
				sagaUserAction: getUserActionName(userAction),
			});
		}
	}
}

function getUserActionName(userAction: PayerOnboardingUserAction): string | undefined {
	return (Object.getOwnPropertyNames(PayerOnboardingUserAction) as Array<keyof typeof PayerOnboardingUserAction>)
		.filter((x: keyof typeof PayerOnboardingUserAction) => userAction instanceof PayerOnboardingUserAction[x])[0];
}

function rehydrateEnterDetailsViewModelFromSagaContext(context: IPayerOnboardingSagaContext): PageEnterDetailsViewModel {
	const { mainViewModel, appState } = context;

	return new PageEnterDetailsViewModel(
		appState.firstName,
		appState.lastName,
		appState.enteredEmailAddress,
		appState.enteredMobileNumber,
		appState.country,
		mainViewModel.IsAnonymousSignUp,
	);
}
